import { api } from './api.js';
import type { Media } from './queries.js';

/**
 * ย่อและแปลงรูปเป็น WebP ในเบราว์เซอร์ก่อนอัปโหลด
 *
 * ทำไมทำฝั่ง client:
 *   sharp เป็น native binary ที่ทำให้ Cloud Function หนักและ cold start ช้าลงมาก
 *   ขณะที่เบราว์เซอร์ทำงานนี้ได้ฟรีด้วย canvas — และยังลด egress ขาขึ้นให้ลูกค้าด้วย
 *   ผลลัพธ์: รูป 4MB จากมือถือกลายเป็น ~250KB ก่อนออกจากเครื่องผู้ใช้
 *
 * ข้อจำกัดที่ยอมรับ: ไม่มี variant หลายขนาดใน Phase 1 (ข้อ 6.4 วางไว้เป็น worker
 * แยกใน Phase 2) — ตอนนี้เก็บไฟล์เดียวที่ย่อแล้วซึ่งพอสำหรับทุกจุดที่ใช้จริง
 */
const MAX_DIMENSION = 1600;
const WEBP_QUALITY = 0.82;

export interface PreparedImage {
  blob: Blob;
  mimeType: string;
  fileName: string;
  width: number | null;
  height: number | null;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('เปิดไฟล์รูปไม่ได้')); };
    img.src = url;
  });
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  // GIF อาจเป็นภาพเคลื่อนไหว — แปลงแล้วจะเหลือเฟรมเดียว จึงส่งต้นฉบับไป
  // PDF และไฟล์อื่นก็ส่งต้นฉบับ
  if (file.type === 'image/gif' || !file.type.startsWith('image/')) {
    return { blob: file, mimeType: file.type, fileName: file.name, width: null, height: null };
  }

  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas ใช้ไม่ได้');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY),
    );
    // canvas ยัง strip EXIF ให้โดยอัตโนมัติ (ข้อ 5.3 "strip EXIF")
    if (!blob) throw new Error('แปลงรูปไม่สำเร็จ');

    // ถ้าแปลงแล้วใหญ่กว่าเดิม (เช่นรูปที่บีบมาดีอยู่แล้ว) ใช้ต้นฉบับดีกว่า
    if (blob.size >= file.size && scale === 1) {
      return { blob: file, mimeType: file.type, fileName: file.name, width, height };
    }

    return {
      blob,
      mimeType: 'image/webp',
      fileName: file.name.replace(/\.[^.]+$/, '') + '.webp',
      width,
      height,
    };
  } catch {
    // เบราว์เซอร์ไม่รองรับ WebP หรือรูปเสีย — ส่งต้นฉบับไปแทนที่จะล้มทั้งการอัปโหลด
    return { blob: file, mimeType: file.type, fileName: file.name, width: null, height: null };
  }
}

async function md5Hex(blob: Blob): Promise<string | null> {
  // SubtleCrypto ไม่มี MD5 — ใช้ SHA-256 แล้วตัด 32 ตัวแรกเป็น fingerprint แทน
  // จุดประสงค์คือ "ตรวจไฟล์ซ้ำภายใน tenant เดียวกัน" ไม่ใช่ความปลอดภัย
  try {
    const buf = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('').slice(0, 32);
  } catch {
    return null;
  }
}

export interface UploadResult { media: Media; duplicate: boolean }

/** ข้อ 6.4 — ขอ signed URL → PUT ตรงไป GCS → แจ้ง complete */
export async function uploadFile(
  file: File,
  opts: { blogId?: string; onProgress?: (pct: number) => void } = {},
): Promise<UploadResult> {
  const prepared = await prepareImage(file);
  opts.onProgress?.(10);

  const checksum = await md5Hex(prepared.blob);

  const ticket = await api.post<{
    data: Media; mediaId: string; uploadUrl: string | null;
    headers: Record<string, string>; duplicate: boolean;
  }>('/v1/media/upload-url', {
    fileName: prepared.fileName,
    mimeType: prepared.mimeType,
    sizeBytes: prepared.blob.size,
    blogId: opts.blogId ?? null,
    checksumMd5: checksum,
  });

  if (ticket.duplicate || !ticket.uploadUrl) {
    opts.onProgress?.(100);
    return { media: ticket.data, duplicate: true };
  }

  opts.onProgress?.(30);

  const put = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: ticket.headers,
    body: prepared.blob,
  });
  if (!put.ok) {
    throw new Error(`อัปโหลดไปยัง storage ไม่สำเร็จ (${put.status})`);
  }

  opts.onProgress?.(80);

  const done = await api.post<{ data: Media }>(`/v1/media/${ticket.mediaId}/complete`, {
    width: prepared.width,
    height: prepared.height,
    altText: null,
  });

  opts.onProgress?.(100);
  return { media: done.data, duplicate: false };
}

export const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif,image/avif,application/pdf';
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
